/**
 * src/components/feature/viewer/ViewerFooter.js
 * 
 * Standardized Viewer Footer (Engagement Hub)
 * Implements:
 * 1. Navigation (Prev/Next/Last) - Large Touch Targets
 * 2. Engagement (Optimistic Like, Rating Slot)
 * 3. Comment Access
 * 4. Atomic Reward Verification
 */
import { ContentService } from "../../../services/content.service.js";

export class ViewerFooter {
    constructor(container, {
        seriesId,
        episodeId,
        seriesData, // { title, authorId, etc. }
        hasPrev,
        hasNext,
        onPrev,
        onNext
    }) {
        this.container = container;
        this.props = { seriesId, episodeId, seriesData, hasPrev, hasNext, onPrev, onNext };

        // State
        this.liked = false;
        this.likeCount = 0;
        this.isProcessing = false;

        // [FIX] Interaction Lock Flag
        // Prevents server sync from overwriting user's optimistic action.
        this.hasInteracted = false;

        this.init();
    }

    async init() {
        this.render();
        this.bindEvents();
        await this.syncLikeStatus();
    }

    render() {
        // Clear previous content
        this.container.innerHTML = '';

        const { hasPrev, hasNext } = this.props;

        const html = `
            <div class="w-full border-t font-sans pb-10 transition-colors duration-300" 
                 style="background-color: var(--footer-bg, transparent); border-color: var(--footer-border, rgba(255,255,255,0.1)); color: var(--footer-text-primary, #e4e4e7);">
                
                <!-- 1. Navigation Layer -->
                <div class="px-4 py-8 max-w-lg mx-auto flex justify-between items-center gap-4 border-b" style="border-color: var(--footer-border, rgba(255,255,255,0.1));">
                    <button id="vf-btn-prev" class="flex-1 py-4 rounded-xl font-bold text-base transition-all active:scale-95 ${!hasPrev ? 'opacity-30 cursor-not-allowed' : ''}" 
                            style="background-color: var(--footer-btn-bg, #27272a); color: var(--footer-btn-text, #e4e4e7);"
                            ${!hasPrev ? 'disabled' : ''}>
                        <div class="flex items-center justify-center gap-2">
                            <span class="material-symbols-outlined text-xl">arrow_back</span>
                            이전화
                        </div>
                    </button>
                    
                    <button id="vf-btn-next" class="flex-1 py-4 rounded-xl bg-indigo-600 text-white font-bold text-base shadow-lg shadow-indigo-500/20 hover:bg-indigo-500 hover:shadow-indigo-500/40 transition-all active:scale-95 ${!hasNext ? 'opacity-90' : ''}">
                        <div class="flex items-center justify-center gap-2">
                            ${hasNext ? '다음화' : '마지막 화'}
                            <span class="material-symbols-outlined text-xl">${hasNext ? 'arrow_forward' : 'check_circle'}</span>
                        </div>
                    </button>
                </div>

                <!-- 2. Engagement Layer (Like) -->
                <div class="py-10 flex flex-col items-center justify-center gap-6 animate-fade-in-up">
                    <button id="vf-btn-like" class="group relative flex flex-col items-center justify-center gap-3 transition-transform active:scale-90">
                        <!-- Ripple -->
                        <div class="absolute inset-0 bg-red-500/20 rounded-full blur-xl opacity-0 scale-50 group-hover:opacity-100 group-hover:scale-110 transition-all duration-500"></div>
                        
                        <!-- Heart Icon -->
                        <div id="vf-heart-icon-container" class="w-20 h-20 rounded-full flex items-center justify-center shadow-inner ring-1 ring-white/5 relative z-10 transition-all duration-300"
                             style="background-color: var(--footer-btn-bg, #27272a); color: var(--footer-icon-default, #d4d4d8);">
                             <span id="vf-heart-icon" class="material-symbols-outlined text-4xl transition-all duration-300" style="font-variation-settings: 'FILL' 0, 'wght' 400;">favorite</span>
                        </div>
                        
                        <!-- Label & Count -->
                        <div class="text-center z-10">
                            <div class="text-xs font-bold uppercase tracking-widest mb-1 transition-colors" style="color: var(--footer-text-secondary, #a1a1aa);">이 에피소드 좋아요</div>
                            <div id="vf-like-count" class="text-sm font-black font-mono transition-colors" style="color: var(--footer-text-primary, #e4e4e7);">${this.likeCount || 0}</div>
                        </div>
                    </button>
                </div>

                <!-- 3. Rating Slot (Injected by Parent) -->
                <div id="vf-rating-slot" class="w-full flex justify-center"></div>

                <!-- 4. Comments Slot (Injected by Parent) -->
                <div id="vf-comment-slot" class="w-full border-t" style="border-color: var(--footer-border, rgba(255,255,255,0.1)); background-color: rgba(0,0,0,0.02);"></div>

                <!-- 5. Metadata (ISBN/UCI) -->
                <div class="text-center py-8 text-[10px] font-mono flex flex-col gap-1 opacity-50" style="color: var(--footer-text-secondary, #a1a1aa);">
                    ${this.props.seriesData?.isbn ? `<span>ISBN: ${this.props.seriesData.isbn}</span>` : ''}
                    ${this.props.seriesData?.uci ? `<span>UCI: ${this.props.seriesData.uci}</span>` : ''}
                </div>
            </div>
        `;

        this.container.innerHTML = html;

        // Cache Elements
        this.btnLike = this.container.querySelector('#vf-btn-like');
        this.heartContainer = this.container.querySelector('#vf-heart-icon-container');
        this.heartIcon = this.container.querySelector('#vf-heart-icon');
        this.countEl = this.container.querySelector('#vf-like-count');
        this.btnPrev = this.container.querySelector('#vf-btn-prev');
        this.btnNext = this.container.querySelector('#vf-btn-next');
    }

    bindEvents() {
        // Navigation
        this.btnPrev.onclick = () => {
            if (this.props.hasPrev && this.props.onPrev) this.props.onPrev();
        };
        this.btnNext.onclick = () => {
            if (this.props.onNext) this.props.onNext();

            // [NEW] Completion Toast for Last Episode
            if (!this.props.hasNext) {
                window.Modal.toast("🎉 이 작품의 마지막 화입니다. 완독하셨습니다!");
            }
        };

        // Like Button
        this.btnLike.onclick = () => this.handleLike();
    }

    // --- Engagement Logic (Optimistic UI) ---

    async syncLikeStatus() {
        if (!window.user) return;

        // [FIX] Pass Episode ID for Granular Check
        const liked = await ContentService.checkLikeStatus(window.user.uid, this.props.seriesId, this.props.episodeId);

        // [FIX] Interaction Lock
        // If user already interacted (clicked like), do NOT overwrite with server state.
        // Server state is "past" state, user action is "future" state.
        if (this.hasInteracted) {
            console.log("[ViewerFooter] Sync ignored due to user interaction.");
            return;
        }

        this.liked = liked;

        // 2. Get Fresh Count (Optional, usually passed in props but good to refresh)
        // For now, we trust props.seriesData or fetch fresh.
        // Let's rely on passed seriesData stats initially to avoid extra read.
        this.likeCount = this.props.seriesData?.stats?.likeCount || 0;
        if (this.props.seriesData?.likeCount) this.likeCount = this.props.seriesData.likeCount; // Fallback

        this.updateLikeVisuals();
    }

    updateLikeVisuals() {
        // We use inline styles for dynamic overrides on top of CSS vars
        if (this.liked) {
            // Heart Container: Red Highlight
            this.heartContainer.style.backgroundColor = 'rgba(239, 68, 68, 0.1)'; // red-500/10
            this.heartContainer.style.color = '#ef4444'; // red-500
            this.heartContainer.classList.add('ring-red-500/50');

            // Icon
            this.heartIcon.style.fontVariationSettings = "'FILL' 1, 'wght' 600";
            this.heartIcon.classList.add('animate-heart-beat');

            // Count
            this.countEl.style.color = '#ef4444'; // red-500
        } else {
            // Revert to CSS Variables (Default Theme)
            this.heartContainer.style.backgroundColor = 'var(--footer-btn-bg, #27272a)';
            this.heartContainer.style.color = 'var(--footer-icon-default, #d4d4d8)';
            this.heartContainer.classList.remove('ring-red-500/50');

            // Icon
            this.heartIcon.style.fontVariationSettings = "'FILL' 0, 'wght' 400";
            this.heartIcon.classList.remove('animate-heart-beat');

            // Count
            this.countEl.style.color = 'var(--footer-text-primary, #e4e4e7)';
        }
        this.countEl.innerText = this.likeCount.toLocaleString();
    }

    async handleLike() {
        if (this.isProcessing) return;
        if (!window.user) {
            if (await window.Modal.confirm("로그인이 필요한 서비스입니다.\n로그인하시겠습니까?")) {
                window.router.navigate('/login');
            }
            return;
        }

        // [FIX] Set Flag immediately
        this.hasInteracted = true;
        this.isProcessing = true;

        // 1. Optimistic Update
        const previousLiked = this.liked;
        const previousCount = this.likeCount;

        this.liked = !this.liked;
        this.likeCount = this.liked ? this.likeCount + 1 : Math.max(0, this.likeCount - 1);

        this.updateLikeVisuals();

        // 2. API Call
        try {
            const actualLikedState = await ContentService.toggleLike(
                window.user.uid,
                this.props.seriesId,
                {
                    ...this.props.seriesData,
                    title: this.props.seriesData.seriesTitle || this.props.seriesData.title
                },
                this.liked,
                this.props.episodeId // [FIX] Pass Episode ID
            );

            // 3. Verification
            if (actualLikedState !== this.liked) {
                // Determine if this was a server override or error
                console.warn("[ViewerFooter] Server returned different like state:", actualLikedState);
                this.liked = actualLikedState;
                this.updateLikeVisuals();
            } else {
                if (this.liked) {
                    window.Modal.toast("좋아요! ❤️"); // Shortened for punchy feel
                }
            }

        } catch (e) {
            console.error("[ViewerFooter] Like Failed:", e);
            // Revert
            this.liked = previousLiked;
            this.likeCount = previousCount;
            this.updateLikeVisuals();
            window.Modal.toast("오류가 발생했습니다. 잠시 후 다시 시도해주세요.");
        } finally {
            this.isProcessing = false;
        }
    }
}
